import { Link, NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  Beaker,
  Rocket,
  BarChart3,
  GitCompareArrows,
  Cloud,
  Zap,
  BellRing,
  CalendarClock,
  Settings,
  ShieldCheck,
  Sun,
  Moon,
  Monitor,
  FileCode2,
  Star,
  ScrollText,
  Plug,
} from 'lucide-react';
import { clsx } from 'clsx';
import { useThemeStore } from '@/stores/themeStore';
import { useAuthStore } from '@/stores/authStore';

const NAV_ITEMS = [
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/plans', label: 'Test Plans', icon: Beaker },
  { to: '/runs', label: 'Runs', icon: Rocket },
  { to: '/runs/compare', label: 'Compare', icon: GitCompareArrows },
  { to: '/reports', label: 'Reports', icon: BarChart3 },
  { to: '/baseline', label: 'Baseline', icon: Star },
  { to: '/environments', label: 'Environments', icon: Cloud },
  { to: '/load-profiles', label: 'Load Profiles', icon: Zap },
  { to: '/alerts', label: 'Alerts', icon: BellRing },
  { to: '/schedules', label: 'Schedules', icon: CalendarClock },
  { to: '/templates', label: 'Templates', icon: FileCode2 },
  { to: '/mcp', label: 'MCP', icon: Plug },
];

const BOTTOM_NAV = [
  { to: '/audit-logs', label: 'Audit Logs', icon: ScrollText, superOnly: true },
  { to: '/admin', label: 'Admin', icon: ShieldCheck },
  { to: '/settings', label: 'Settings', icon: Settings },
];

function MeridianMark({ className }: { className?: string }) {
  return (
    <svg
      viewBox="90 100 220 210"
      className={className}
      aria-hidden="true"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <linearGradient id="sb_arcGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#67e8f9" />
          <stop offset="100%" stopColor="#06b6d4" />
        </linearGradient>
        <linearGradient id="sb_tealGrad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#06b6d4" />
          <stop offset="100%" stopColor="#8b5cf6" />
        </linearGradient>
      </defs>
      <path d="M200 200 m-90 0 a90 90 0 1 1 180 0" stroke="url(#sb_arcGrad)" strokeWidth="3.5" strokeLinecap="round" />
      <path
        d="M200 200 m-68 0 a68 68 0 1 1 136 0"
        stroke="#06b6d4"
        strokeWidth="1.5"
        strokeLinecap="round"
        opacity="0.4"
      />
      <line x1="200" y1="108" x2="200" y2="200" stroke="#67e8f9" strokeWidth="2.5" strokeLinecap="round" />
      <line
        x1="200"
        y1="200"
        x2="200"
        y2="292"
        stroke="#06b6d4"
        strokeWidth="2.5"
        strokeLinecap="round"
        opacity="0.3"
      />
      <line x1="200" y1="200" x2="254" y2="136" stroke="#67e8f9" strokeWidth="3" strokeLinecap="round" />
      <circle cx="254" cy="136" r="4" fill="#67e8f9" />
      <circle cx="200" cy="200" r="7" fill="url(#sb_tealGrad)" />
      <circle cx="200" cy="200" r="3.5" fill="#cffafe" />
    </svg>
  );
}

export function Sidebar() {
  const { theme, setTheme } = useThemeStore();
  const user = useAuthStore((s) => s.user);
  return (
    <aside className="w-48 flex-shrink-0 bg-background-secondary border-r border-border flex flex-col">
      {/* ── Brand ── */}
      <Link
        to="/"
        className="h-14 flex items-center gap-2.5 px-2.5 border-b border-border hover:bg-background-elevated transition-colors"
      >
        <div className="w-8 h-8 rounded-lg bg-brand-muted flex items-center justify-center flex-shrink-0 shadow-glow-sm">
          <MeridianMark className="w-6 h-6" />
        </div>
        <div className="flex flex-col leading-tight">
          <span className="text-sm font-semibold text-meridian tracking-tight">Meridian</span>
          <span className="text-[8px] font-medium tracking-[0.18em] text-brand/60 uppercase">by Saviynt</span>
        </div>
      </Link>

      {/* ── Main nav ── */}
      <nav className="flex-1 py-3 px-1.5 space-y-1 overflow-y-auto">
        {NAV_ITEMS.map(({ to, label, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            className={({ isActive }) =>
              clsx(
                'relative flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-[13px] transition-all duration-150 group/item',
                isActive
                  ? 'bg-brand-muted text-brand-light font-medium shadow-glow-sm'
                  : 'text-text-secondary hover:text-text-primary hover:bg-background-elevated hover:scale-[1.02]',
              )
            }
          >
            {({ isActive }) => (
              <>
                {isActive && (
                  <span className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-r-full bg-brand shadow-glow-sm" />
                )}
                <Icon className="w-[18px] h-[18px] flex-shrink-0" strokeWidth={isActive ? 2.2 : 1.8} />
                <span className="">{label}</span>
              </>
            )}
          </NavLink>
        ))}
      </nav>

      {/* ── Bottom nav ── */}
      <div className="border-t border-border">
        <div className="py-2 px-1.5 space-y-1">
          {BOTTOM_NAV.filter((n) => !n.superOnly || user?.is_superuser).map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                clsx(
                  'relative flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-[13px] transition-all duration-150',
                  isActive
                    ? 'bg-brand-muted text-brand-light font-medium shadow-glow-sm'
                    : 'text-text-secondary hover:text-text-primary hover:bg-background-elevated hover:scale-[1.02]',
                )
              }
            >
              {({ isActive }) => (
                <>
                  {isActive && (
                    <span className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-r-full bg-brand shadow-glow-sm" />
                  )}
                  <Icon className="w-[18px] h-[18px] flex-shrink-0" strokeWidth={isActive ? 2.2 : 1.8} />
                  <span className="">{label}</span>
                </>
              )}
            </NavLink>
          ))}
        </div>
        <div className="px-2 pb-2 pt-1">
          <div className="flex items-center bg-background-elevated rounded-lg p-0.5 gap-0.5">
            <button
              onClick={() => setTheme('light')}
              className={clsx(
                'p-1.5 rounded-md transition-all',
                theme === 'light'
                  ? 'bg-brand-muted text-brand-light shadow-glow-sm'
                  : 'text-text-muted hover:text-text-secondary',
              )}
              title="Light"
            >
              <Sun className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => setTheme('system')}
              className={clsx(
                'p-1.5 rounded-md transition-all',
                theme === 'system'
                  ? 'bg-brand-muted text-brand-light shadow-glow-sm'
                  : 'text-text-muted hover:text-text-secondary',
              )}
              title="System"
            >
              <Monitor className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => setTheme('dark')}
              className={clsx(
                'p-1.5 rounded-md transition-all',
                theme === 'dark'
                  ? 'bg-brand-muted text-brand-light shadow-glow-sm'
                  : 'text-text-muted hover:text-text-secondary',
              )}
              title="Dark"
            >
              <Moon className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
        <div className="px-3 pb-3 pt-1">
          <img
            src="/saviynt-logo.svg"
            alt="Saviynt"
            className="h-3 opacity-20"
            style={{ filter: 'invert(70%) sepia(60%) saturate(500%) hue-rotate(150deg)' }}
          />
        </div>
      </div>
    </aside>
  );
}
